import { useSelector, useDispatch } from 'react-redux';
import {
    setDashboardLoading, 
    setDashboardError, 
    setDashboardData,
    setProjectDashboardLoading,
    setProjectDashboardError,
    setProjectDashboardData,
    setRoadmapDashboardLoading,
    setRoadmapDashboardError,
    setRoadmapDashboardData,
    setRoadmapGroupDashboardLoading,
    setRoadmapGroupDashboardError,
    setRoadmapGroupDashboardData,
    setRefreshStatus,
    setRefreshStatusLoading,
    setRefreshStatusError
} from '../redux/slices/dashboard';
import axios from '../utils/axiosConfig';
import { notify } from 'react-notify-toast';

const useDashboardService = () => {
    const dispatch = useDispatch();

    const showError = (error: any) => {
        if(error?.response?.data?.error){
            notify.show(error.response.data.error, "error", 3000);
        }
        if(typeof error?.response?.data?.detail === "string" && error?.response?.data?.detail){
            notify.show(error.response.data.detail, "error", 3000);
        }
        if(error.message){
            notify.show(error.message, "error", 3000);
        }
    };

    // Get Organization Dashboard
    const fetchDashboard = async () => {
        dispatch(setDashboardLoading(true));
        try {
            const response = await axios.get('/dashboard');
            dispatch(setDashboardData(response.data));
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setDashboardError(error));
        } finally {
            dispatch(setDashboardLoading(false));
        }
    };

    // Get Project Dashboard
    const fetchProjectDashboard = async (projectId: string) => {
        dispatch(setProjectDashboardLoading(true));
        try {
            const response = await axios.get(`/dashboard/project/${projectId}`);
            dispatch(setProjectDashboardData(response.data));
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setProjectDashboardError(error));
        } finally {
            dispatch(setProjectDashboardLoading(false));
        }
    };

    // Get Roadmap Dashboard
    const fetchRoadmapDashboard = async (roadmapId: string) => {
        dispatch(setRoadmapDashboardLoading(true));
        try {
            const response = await axios.get(`/dashboard/roadmap/${roadmapId}`);
            dispatch(setRoadmapDashboardData(response.data));
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setRoadmapDashboardError(error));
        } finally {
            dispatch(setRoadmapDashboardLoading(false));
        }
    };

    // Get Roadmap Group Dashboard
    const fetchRoadmapGroupDashboard = async (groupId: string) => {
        dispatch(setRoadmapGroupDashboardLoading(true));
        try {
            const response = await axios.get(`/dashboard/roadmap-group/${groupId}`);
            dispatch(setRoadmapGroupDashboardData(response.data));
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setRoadmapGroupDashboardError(error));
        } finally {
            dispatch(setRoadmapGroupDashboardLoading(false));
        }
    };

    // Get Refresh Status for an entity (project, roadmap, or roadmap group)
    const checkRefreshStatus = async (entityId: string) => {
        dispatch(setRefreshStatusLoading(true));
        try {
            const response = await axios.get(`/dashboard/refresh/status/${entityId}`);
            dispatch(setRefreshStatus({ [entityId]: response.data }));
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setRefreshStatusError(error));
        } finally {
            dispatch(setRefreshStatusLoading(false));
        }
    };

    // Force refresh a roadmap group
    const refreshRoadmapGroup = async (groupId: string) => {
        dispatch(setRoadmapGroupDashboardLoading(true));
        try {
            const response = await axios.get(`/dashboard/roadmap-group/${groupId}`, {
                params: { force_refresh: true }
            });
            dispatch(setRoadmapGroupDashboardData(response.data));
            return response.data;
        } catch (error) {
            showError(error);
            dispatch(setRoadmapGroupDashboardError(error));
        } finally {
            dispatch(setRoadmapGroupDashboardLoading(false));
        }
    };

    return {
        fetchDashboard,
        fetchProjectDashboard,
        fetchRoadmapDashboard,
        fetchRoadmapGroupDashboard,
        checkRefreshStatus,
        refreshRoadmapGroup
    };
};

export default useDashboardService;
export { useDashboardService }; 